/**
 * Profile360 Configuration Management
 * Centralized configuration for the Profile360 system
 */

import queryMappings from './queryMappings.json';
import { config } from '../../../config';

/**
 * Profile360 Configuration class
 */
class Profile360Config {
  constructor() {
    this.config = {
      // API Configuration
      api: {
        timeout: 30000, // 30 seconds
        retryAttempts: 3,
        retryDelay: 1000, // 1 second
        endpoints: {
          base: config.baseAPIUrl,
          knowledgeGraph: '/api/knowledge-graph',
          users: '/users',
          applications: '/api/applications',
          dormantUsers: '/api/dormant-users',
          staleApps: '/api/users/{user_email}/stale-apps'
        }
      },

      // Query Configuration
      queries: {
        mappings: queryMappings,
        defaultTimeout: 30000,
        maxResults: 1000,
        fallbackEnabled: true
      },

      // Visualization Configuration
      visualization: {
        graph: {
          nodeSize: 8,
          linkDistance: 100,
          chargeStrength: -300,
          maxNodes: 500,
          maxLinks: 1000,
          colors: {
            Department: '#1abc9c',
            Location: '#2ecc71',
            Application: '#3498db',
            User: '#9b59b6',
            Role: '#f1c40f',
            Team: '#e67e22',
            Resource: '#e74c3c',
            Company: '#95a5a6',
            default: '#3498db'
          }
        },
        table: {
          pageSize: 10,
          maxRows: 1000,
          sortable: true,
          filterable: true
        }
      },

      // Performance Configuration
      performance: {
        caching: {
          enabled: true,
          ttl: 300000, // 5 minutes
          maxSize: 100 // Max cached items
        },
        debounce: {
          searchDelay: 500, // ms
          filterDelay: 300 // ms
        }
      },

      // Feature Flags
      features: {
        naturalLanguageQuery: true,
        cypherQuery: true,
        graphClustering: true,
        realTimeUpdates: false,
        advancedFilters: true,
        exportData: true,
        aiInsights: true
      },

      // UI Configuration
      ui: {
        theme: 'light',
        compactMode: false,
        showDebugInfo: process.env.NODE_ENV === 'development',
        animations: {
          enabled: true,
          duration: 400
        },
        notifications: {
          position: 'top-right',
          autoClose: 5000
        }
      }
    };

    // Load any environment-specific overrides
    this.loadEnvironmentConfig();
  }

  /**
   * Load environment-specific configuration
   */
  loadEnvironmentConfig() {
    // Override API base URL if provided
    if (process.env.REACT_APP_PROFILE360_API_URL) {
      this.config.api.endpoints.base = process.env.REACT_APP_PROFILE360_API_URL;
    }

    // Override timeout if provided
    if (process.env.REACT_APP_PROFILE360_TIMEOUT) {
      this.config.api.timeout = parseInt(process.env.REACT_APP_PROFILE360_TIMEOUT);
    }

    // Override max results if provided
    if (process.env.REACT_APP_PROFILE360_MAX_RESULTS) {
      this.config.queries.maxResults = parseInt(process.env.REACT_APP_PROFILE360_MAX_RESULTS);
    }

    // Disable features in production if needed
    if (process.env.NODE_ENV === 'production') {
      this.config.ui.showDebugInfo = false;
      this.config.features.realTimeUpdates = false;
    }
  }

  /**
   * Get configuration value by path
   * @param {string} path - Dot-separated path (e.g., 'api.timeout')
   * @param {*} defaultValue - Default value if path not found
   * @returns {*} Configuration value
   */
  get(path, defaultValue = null) {
    const keys = path.split('.');
    let current = this.config;

    for (const key of keys) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        return defaultValue;
      }
    }

    return current;
  }

  /**
   * Set configuration value by path
   * @param {string} path - Dot-separated path
   * @param {*} value - Value to set
   */
  set(path, value) {
    const keys = path.split('.');
    const lastKey = keys.pop();
    let current = this.config;

    for (const key of keys) {
      if (!(key in current) || typeof current[key] !== 'object') {
        current[key] = {};
      }
      current = current[key];
    }

    current[lastKey] = value;
  }

  /**
   * Get API endpoint URL
   * @param {string} endpoint - Endpoint name
   * @returns {string} Full URL
   */
  getApiEndpoint(endpoint) {
    const base = this.get('api.endpoints.base');
    const path = this.get(`api.endpoints.${endpoint}`, endpoint);
    return `${base}${path}`;
  }

  /**
   * Get query mapping by ID
   * @param {string} queryId - Query ID
   * @returns {Object|null} Query mapping
   */
  getQueryMapping(queryId) {
    const categories = this.get('queries.mappings.categories', {});
    
    for (const category of Object.values(categories)) {
      const query = category.queries?.find(q => q.id === queryId);
      if (query) {
        return query;
      }
    }
    
    return null;
  }

  /**
   * Get all query mappings
   * @returns {Array} All query mappings
   */
  getAllQueryMappings() {
    const categories = this.get('queries.mappings.categories', {});
    const allQueries = [];
    
    for (const category of Object.values(categories)) {
      if (category.queries) {
        allQueries.push(...category.queries);
      }
    }
    
    return allQueries;
  }

  /**
   * Get node color by type
   * @param {string} nodeType - Node type/label
   * @returns {string} Color hex code
   */
  getNodeColor(nodeType) {
    return this.get(`visualization.graph.colors.${nodeType}`) || 
           this.get('visualization.graph.colors.default');
  }

  /**
   * Check if feature is enabled
   * @param {string} featureName - Feature name
   * @returns {boolean} True if enabled
   */
  isFeatureEnabled(featureName) {
    return this.get(`features.${featureName}`, false);
  }

  /**
   * Get performance setting
   * @param {string} setting - Setting name
   * @returns {*} Setting value
   */
  getPerformanceSetting(setting) {
    return this.get(`performance.${setting}`);
  }

  /**
   * Update query mappings
   * @param {Object} newMappings - New query mappings
   */
  updateQueryMappings(newMappings) {
    this.set('queries.mappings', newMappings);
  }

  /**
   * Reset configuration to defaults
   */
  reset() {
    this.loadEnvironmentConfig();
  }

  /**
   * Export current configuration
   * @returns {Object} Current configuration
   */
  export() {
    return JSON.parse(JSON.stringify(this.config));
  }

  /**
   * Import configuration
   * @param {Object} newConfig - Configuration to import
   */
  import(newConfig) {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Validate configuration
   * @returns {Object} Validation result
   */
  validate() {
    const errors = [];
    const warnings = [];

    // Validate API configuration
    if (!this.get('api.endpoints.base')) {
      errors.push('API base URL is required');
    }

    // Validate timeout values
    const timeout = this.get('api.timeout');
    if (timeout && (timeout < 1000 || timeout > 120000)) {
      warnings.push('API timeout should be between 1-120 seconds');
    }

    // Validate max results
    const maxResults = this.get('queries.maxResults');
    if (maxResults && maxResults > 10000) {
      warnings.push('Max results is very high, may impact performance');
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }
}

// Create singleton instance
const profile360Config = new Profile360Config();

export default profile360Config;
